/**
 * Zentrale Quelle für Spieler-Auszeichnungs-Badges (Alpha-Tester, Früher Vogel, Crime-Rang).
 * Ersetzt zwei vormals unabhängige, widersprüchliche Implementierungen (profile.ts, community.ts)
 * — u.a. wurde derselbe Crime-Rang dort in unterschiedlichen Farben angezeigt.
 * Dupliziert die Backend-Definitionen aus award.definitions.ts (kein Shared-Import für reine
 * Anzeige-Werte, etabliertes Repo-Muster) — bei Änderung dort auch hier nachziehen.
 */

export interface AwardBadge {
  awardKey: string;
  label: string;
  colorVar: string;
}

const ALPHA_TESTER_BADGE = { label: 'ALPHA', colorVar: '--accent-gold' };
const FRUEHER_VOGEL_BADGE = { label: 'FRÜHER VOGEL', colorVar: '--accent-green' };
/** NT-PRESTIGE-02 — Spiegel von award.definitions.ts::getPrestigeBadgeLabel/getPrestigeColorVar. */
const PRESTIGE_BADGE_LABELS = ['PRESTIGE I', 'PRESTIGE II', 'PRESTIGE III'] as const;
const PRESTIGE_BADGE_COLOR_VARS = ['--accent-prestige', '--accent-prestige-2', '--accent-prestige-3'] as const;

/** Farbtoken je Prestige-Stufe (I Silber, II Türkis, III Rosé). Für Badge, Sidebar, Rangliste. */
export function getPrestigeColorVar(prestigeRank: number): string {
  return PRESTIGE_BADGE_COLOR_VARS[Math.min(Math.max(prestigeRank, 1), PRESTIGE_BADGE_COLOR_VARS.length) - 1] ?? '--accent-prestige';
}

/** Anzeige-Priorität, wenn mehr Badges vorhanden sind als das Limit (2) zulässt. */
const BADGE_DISPLAY_PRIORITY: readonly string[] = ['PRESTIGE', 'ALPHA_TESTER', 'FRUEHER_VOGEL', 'CRIME_RANK'];

export const MAX_VISIBLE_BADGES = 2;

/**
 * Baut aus den drei bekannten Quellen die anzuzeigenden Badges zusammen — max.
 * MAX_VISIBLE_BADGES, nach BADGE_DISPLAY_PRIORITY sortiert/gekappt.
 *
 * Die Crime-Rang-Farbe kommt fertig vom Server (`/auth/me` → crimeRankColorVar, berechnet in
 * award.definitions.ts::getCrimeRankBadgeColor aus der Rang-Position). Bewusst KEINE eigene
 * Rang-Farbtabelle mehr hier — die alte feste Key-Liste ließ nach einem Umbau der Ränge alle
 * neuen Ränge verschwinden. null/fehlend = kein Badge (z. B. Normalbürger).
 */
export function resolvePlayerBadges(input: {
  isAlphaTester?: boolean | undefined;
  hasFrueherVogelBadge?: boolean | undefined;
  crimeRankLabel?: string | undefined;
  crimeRankColorVar?: string | null | undefined;
  prestigeRank?: number | undefined;
}): AwardBadge[] {
  const candidates: AwardBadge[] = [];

  const rank = input.prestigeRank ?? 0;
  if (rank > 0) {
    const label = PRESTIGE_BADGE_LABELS[Math.min(rank, PRESTIGE_BADGE_LABELS.length) - 1];
    if (label !== undefined) candidates.push({ awardKey: 'PRESTIGE', label, colorVar: getPrestigeColorVar(rank) });
  }

  if (input.isAlphaTester === true) {
    candidates.push({ awardKey: 'ALPHA_TESTER', ...ALPHA_TESTER_BADGE });
  }

  if (input.hasFrueherVogelBadge === true) {
    candidates.push({ awardKey: 'FRUEHER_VOGEL', ...FRUEHER_VOGEL_BADGE });
  }

  const crimeColor = input.crimeRankColorVar;
  if (crimeColor !== undefined && crimeColor !== null && crimeColor !== '' && input.crimeRankLabel !== undefined) {
    candidates.push({ awardKey: 'CRIME_RANK', label: input.crimeRankLabel, colorVar: crimeColor });
  }

  candidates.sort(
    (a, b) => BADGE_DISPLAY_PRIORITY.indexOf(a.awardKey) - BADGE_DISPLAY_PRIORITY.indexOf(b.awardKey),
  );

  return candidates.slice(0, MAX_VISIBLE_BADGES);
}

function esc(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/** Badge-Markup: Border + Textfarbe, kein Fill, UPPERCASE-Label — Muster wie item-badges.ts. */
export function awardBadgeHtml(badge: AwardBadge, cssClass: string): string {
  return `<span class="${cssClass}" style="color:var(${badge.colorVar});border-color:var(${badge.colorVar});">${esc(badge.label)}</span>`;
}

/** Fertiges HTML-Fragment aller anzuzeigenden Badges, leerer String falls keine vorhanden. */
export function renderPlayerBadges(
  input: Parameters<typeof resolvePlayerBadges>[0],
  cssClass: string,
): string {
  return resolvePlayerBadges(input).map((b) => awardBadgeHtml(b, cssClass)).join(' ');
}
